import React, { useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminSignupPage from './pages/AdminSignupPage';
import AdminForgotPasswordPage from './pages/AdminForgotPasswordPage';
import AddProjectPage from './pages/AddProjectPage';
import CompanyInventoryPage from './pages/CompanyInventoryPage';
import CompanyLeadsPage from './pages/CompanyLeadsPage';
import CompanyUploadPage from './pages/CompanyUploadPage';
import CompanyTeamPage from './pages/CompanyTeamPage';
import SearchPage from './pages/SearchPage';
import ProjectsManagementPage from './pages/ProjectsManagementPage';
import PropertyManagementPage from './pages/PropertyManagementPage';
import DatabasePage from './pages/DatabasePage';
import AdminManagementPage from './pages/AdminManagementPage';
import CompaniesPage from './pages/CompaniesPage';
import ApiStatus from './components/ApiStatus';
import './index.css';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<AdminLoginPage />} />
          <Route path="/signup" element={<AdminSignupPage />} />
          <Route path="/forgot-password" element={<AdminForgotPasswordPage />} />
          <Route path="/property/:projectId" element={
            <PrivateRoute><PropertyManagementPage /></PrivateRoute>
          } />
          <Route path="/*" element={
            <PrivateRoute><MainApp /></PrivateRoute>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

function MainApp() {
  const { user, isAdmin, isCompany, logout } = useAuth();
  // Company staff land on their leads — that is what they signed in for. The
  // back office lands on ingestion, as before.
  const [activeTab, setActiveTab] = useState(isCompany ? 'leads' : 'add-project');

  return (
    <div className="container">
      {/* Admin bar with user info + sign out */}
      <div className="admin-bar">
        <span className="admin-bar-user">
          👤 {user?.full_name || user?.email}
          <span className={`admin-bar-role admin-bar-role-${isAdmin ? 'admin' : isCompany ? 'company' : 'broker'}`}>
            {isAdmin ? 'ADMIN' : isCompany ? 'COMPANY' : 'BROKER'}
          </span>
        </span>
        <button className="admin-bar-logout" onClick={logout}>Sign Out</button>
      </div>

      <div className="header">
        <h1>{isCompany ? '🏢 Your brokerage' : '🏗 Real Estate Data Analyzer'}</h1>
        <p>
          {isCompany
            ? 'The leads your advisor qualified, and the people who follow them up.'
            : 'Upload your real estate files and get AI-powered analysis with multiple project support'}
        </p>
      </div>

      {/* Tab Navigation.
          Company staff see only their own surfaces. Hiding the rest is a
          courtesy — the server refuses those routes for them regardless
          (auth/company_route_policy.py). */}
      <div className="tab-navigation">
        {isCompany && (
          <button
            className={`tab-button ${activeTab === 'leads' ? 'active' : ''}`}
            onClick={() => setActiveTab('leads')}
          >
            👥 Leads
          </button>
        )}

        {/* Inventory and Add units are for internal staff too. Their units land
            unowned, which is what our marketplace stock is — and the two-phase
            review here is the part worth having, so there is no reason to send
            our own uploads down a worse path to keep this one "for companies". */}
        <button
          className={`tab-button ${activeTab === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveTab('inventory')}
        >
          🏘 {isCompany ? 'Inventory' : 'Marketplace stock'}
        </button>
        <button
          className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          ⬆️ Add units
        </button>

        {isCompany && (
          <button
            className={`tab-button ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => setActiveTab('team')}
          >
            🧑‍💼 Team
          </button>
        )}
        {!isCompany && (
        <button
          className={`tab-button ${activeTab === 'add-project' ? 'active' : ''}`}
          onClick={() => setActiveTab('add-project')}
        >
          ➕ Add Project
        </button>
        )}
        {!isCompany && (
        <button
          className={`tab-button ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          🔍 Search Projects
        </button>
        )}
        {!isCompany && (
        <button
          className={`tab-button ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          📁 Project Management
        </button>
        )}
        {!isCompany && (
        <button
          className={`tab-button ${activeTab === 'database' ? 'active' : ''}`}
          onClick={() => setActiveTab('database')}
        >
          🗄️ Database
        </button>
        )}
        {isAdmin && (
          <button
            className={`tab-button ${activeTab === 'companies' ? 'active' : ''}`}
            onClick={() => setActiveTab('companies')}
          >
            🏢 Companies
          </button>
        )}
        {isAdmin && (
          <button
            className={`tab-button ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            🛡️ Admin Panel
          </button>
        )}
      </div>

      {/* Render the active page */}
      {activeTab === 'leads' && isCompany && <CompanyLeadsPage />}
      {activeTab === 'inventory' && <CompanyInventoryPage />}
      {activeTab === 'upload' && (
        <CompanyUploadPage onCommitted={() => setActiveTab('inventory')} />
      )}
      {activeTab === 'team' && isCompany && <CompanyTeamPage />}
      {activeTab === 'add-project' && !isCompany && <AddProjectPage />}
      {activeTab === 'search' && !isCompany && <SearchPage />}
      {activeTab === 'projects' && !isCompany && <ProjectsManagementPage />}
      {activeTab === 'database' && !isCompany && <DatabasePage />}
      {activeTab === 'companies' && isAdmin && <CompaniesPage />}
      {activeTab === 'admin' && isAdmin && <AdminManagementPage />}
      {/* API Status Indicator */}
      <ApiStatus />
    </div>
  );
}

export default App;
